import type { NextApiRequest } from "next";

export function getAllCookieNames(req: NextApiRequest): string[] {
  const rawCookies = req.headers.cookie ?? "";
  return rawCookies
    .split(";")
    .map((cookie) => cookie.split("=")[0].trim())
    .filter((name) => name.length > 0);
}

export function getSessionCookie(req: NextApiRequest, cookieName = "sessionID_QRV"): string | undefined {
  const rawCookies = req.headers.cookie ?? "";
  return rawCookies
    .split(";")
    .find((cookie) => cookie.trim().startsWith(`${cookieName}=`));
}

export function getCookieValue(key: string, cookieHeader?: string): string | null {
  const header = cookieHeader ?? (typeof document !== "undefined" ? document.cookie : "");
  if (!header) {
    return null;
  }

  const cookies = header.split("; ");
  for (const cookie of cookies) {
    const [cookieKey, ...cookieValue] = cookie.split("=");
    if (cookieKey === decodeURIComponent(key)) {
      return decodeURIComponent(cookieValue.join("="));
    }
  }
  return null;
}

export function getExpiredCookiesHeader(cookieNames: string[]): string[] {
  return cookieNames.map(
    (name) =>
      `${name}=; Path=/; HttpOnly; Secure; SameSite=None; Expires=Thu, 01 Jan 1970 00:00:00 GMT`
  );
}

type ParsedCookies = {
  rxVisitor?: string;
  traceId_QRV?: string;
  sessionID_QRV?: string;
  MI_SITE?: string;
  languageCode?: string;
};

const getCookieValueFrmHdr = (
  cookieHeader: string,
  key: string
): string | undefined => {
  const escaped = key.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&");
  const pattern = new RegExp(`(?:^|;\\s*)${escaped}=([^;]*)`);
  const match = pattern.exec(cookieHeader);
  return match ? decodeURIComponent(match[1]) : undefined;
};

const getLanguageCodeFromHeader = (cookieHeader: string): string | undefined => {
  return (
    getCookieValueFrmHdr(cookieHeader, "languageCode") 
  );
};

/**
 * SERVER SIDE ONLY / CLIENT (fallback)
 * Reads cookies from request header and caches values
 */
export function parseCookieValues(cookieHeader = ""): ParsedCookies {
  const header = cookieHeader || (typeof document !== "undefined" ? document.cookie : "");
  const parsedCookies: ParsedCookies = {
    rxVisitor: getCookieValueFrmHdr(header, "rxVisitor"),
    traceId_QRV: getCookieValueFrmHdr(header, "traceId_QRV"),
    sessionID_QRV: getCookieValueFrmHdr(header, "sessionID_QRV"),
    MI_SITE: getCookieValueFrmHdr(header, "MI_SITE"),
    languageCode: getLanguageCodeFromHeader(header),
  };

  return parsedCookies;
}

export function getServerCookieValues(
  reqOrCookieHeader?: NextApiRequest | string
): ParsedCookies {
  let parsed = {} as ParsedCookies;

  if (typeof reqOrCookieHeader === "string") {
    // cookie header passed directly
    parsed = parseCookieValues(reqOrCookieHeader);
  } else if (reqOrCookieHeader?.headers?.cookie) {
    parsed = parseCookieValues(reqOrCookieHeader.headers.cookie);
  } else if (typeof document !== "undefined") {
    parsed = parseCookieValues(document.cookie);
  }
  return parsed;
}

